/**
 * 017-style structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/users-table/columns.tsx — the
 * same plain `ColumnDef[]` shape as `../audit/columns.tsx`; the icon/title/body cell is the row of
 * vendor/isomorphic/apps/isomorphic/src/layouts/notification-dropdown.tsx, so a notice looks the same
 * here as in the header's bell.
 *
 * Only the data, columns and labels are ours. Kind, severity and read state are the API's own values:
 * the icon says what happened (`NotificationIcon`, from `kind`), its tint how loud it is.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { PiArrowSquareOutDuotone, PiChecksDuotone } from "react-icons/pi";
import { getStatusBadge, NotificationIcon, type NotificationItem } from "@steelyard/theme";
import { RowActions } from "../../components/table/row-actions";
import { formatMoment } from "../../lib/format";

export const notificationColumns = (open: (item: NotificationItem) => void): ColumnDef<NotificationItem>[] => [
  {
    id: "actions",
    header: "",
    // The row's ⋮ menu: go to what the notice is about (marking it read), or just mark it read.
    cell: ({ row }) => {
      const item = row.original;
      return (
        <RowActions
          name={item.title}
          primary={item.link ? { label: "Open", icon: PiArrowSquareOutDuotone, to: item.link } : undefined}
          more={[!item.read && { label: "Mark as read", icon: PiChecksDuotone, onClick: () => open(item) }]}
        />
      );
    },
  },
  {
    id: "title",
    accessorKey: "title",
    header: "Notification",
    cell: ({ row }) => {
      const item = row.original;
      const text = (
        <span className="flex min-w-0 items-start gap-3">
          <NotificationIcon kind={item.kind} severity={item.severity} />
          <span className="min-w-0">
            <span className={item.read ? "block font-medium text-gray-900 group-hover:underline" : "block font-semibold text-gray-900 group-hover:underline"}>{item.title}</span>
            <span className="block text-xs text-gray-500">{item.body}</span>
          </span>
        </span>
      );
      return item.link ? (
        <Link to={item.link} onClick={() => open(item)} className="group block">
          {text}
        </Link>
      ) : (
        text
      );
    },
  },
  {
    id: "severity",
    accessorKey: "severity",
    header: "Severity",
    size: 130,
    cell: ({ row }) => getStatusBadge(row.original.severity),
  },
  {
    id: "occurredAt",
    accessorKey: "occurredAt",
    header: "When",
    size: 190,
    cell: ({ row }) => <span className="whitespace-nowrap text-gray-600">{formatMoment(row.original.occurredAt)}</span>,
  },
  {
    id: "read",
    accessorFn: (item) => (item.read ? "read" : "unread"),
    header: "Status",
    size: 110,
    cell: ({ row }) => getStatusBadge(row.original.read ? "read" : "unread"),
  },
];
